import type { JSX } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { definitionModule, type Module } from '@artosera/shared';
import styles from './ModuleNonActivePage.module.css';

export interface ModuleNonActivePageProps {
  /** Le module complémentaire que l'URL demande et que la galerie n'a pas. */
  module: Module;
  galerieSlug: string;
  /**
   * `ventes` coupé : lien vers l'archive des factures en lecture seule, pour
   * qui a le droit de les lire (conservation légale).
   */
  lienArchive?: boolean;
}

/**
 * Page « Module non activé » (D21, ARTO-56) : ce qu'on trouve en suivant une
 * URL directe vers un module complémentaire que la galerie n'a pas — ou plus.
 *
 * AUCUNE donnée : la page ne monte aucun écran du module et n'appelle pas
 * l'API, qui refuserait de toute façon (403 `module_inactif`). Elle dit ce
 * que le module fait, et à qui s'adresser pour l'ajouter.
 *
 * Mise en forme : la fiche du module, rangée au classeur — l'onglet dit
 * qu'il n'est pas activé, la fiche dit ce qu'il fait, l'encart dit à qui
 * s'adresser. Ni vitrine commerciale, ni écran d'erreur.
 */
export function ModuleNonActivePage({
  module,
  galerieSlug,
  lienArchive = false,
}: ModuleNonActivePageProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const definition = definitionModule(module);

  return (
    <div className={styles.page} data-testid="module-non-active">
      <div className={styles.fiche}>
        <span className={styles.onglet}>
          <Lock size={12} strokeWidth={2} aria-hidden />
          {t('regie:module.nonActive.etiquette')}
        </span>
        <h1 className={styles.titre}>{t(definition.libelle)}</h1>
        <p className={styles.description}>{t(definition.description)}</p>
        <div className={styles.note}>
          <p>{t('regie:module.nonActive.phrase')}</p>
          {module === 'facturation' ? <p>{t('regie:module.nonActive.ventesConservees')}</p> : null}
        </div>
        <div className={styles.actions}>
          {module === 'facturation' && lienArchive ? (
            <Link className={styles.lien} to={`/${galerieSlug}/archive-factures`}>
              {t('regie:archiveFactures.lien')}
            </Link>
          ) : null}
          <Link className={styles.lien} to={`/${galerieSlug}`}>
            {t('regie:module.retourTableauDeBord')}
          </Link>
        </div>
      </div>
    </div>
  );
}
